import { ref } from 'vue'
import store from '@/store'
import { config } from '@/config.js'
import { useToast } from '@/composables/common/useToast'

// ==================== MODULE-SCOPE: shared singleton ====================
// Shared across all importers of this module (sidebar, familias views).
// See design decision #2: cross-component refresh without adding a new dep.
const pendingCountFamilias = ref(0)
let inFlightFamilias = false
let pendingPromiseFamilias = null

/**
 * Fetch the current count of pending family verifications.
 * Shares an in-flight promise across overlapping callers (no duplicate dispatches).
 * Exported standalone so the sidebar can use it without the full composable factory.
 * @returns {Promise<number>} the current pending count (0 if backend is unreachable)
 */
export async function fetchPendingCountFamilias() {
    if (inFlightFamilias) return pendingPromiseFamilias
    inFlightFamilias = true
    pendingPromiseFamilias = store
        .dispatch(config.getVuexAlias('back.familias.verificacion.pendientes.count'), { filters: {} })
        .then(res => {
            if (res?.data?.count !== undefined) pendingCountFamilias.value = res.data.count
            return pendingCountFamilias.value
        })
        .catch(err => {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[familias] pendingCount fetch failed:', err)
            }
            // Intentionally preserve prior value on error (spec REQ-007 scenario 2).
            return pendingCountFamilias.value
        })
        .finally(() => {
            inFlightFamilias = false
            pendingPromiseFamilias = null
        })
    return pendingPromiseFamilias
}

/**
 * Direct access to the shared pending count ref.
 * Useful when the consumer wants to watch the value instead of awaiting a fetch.
 * @returns {import('vue').Ref<number>}
 */
export function getPendingCountFamilias() {
    return pendingCountFamilias
}

/**
 * Extract a user-facing error message from an API error response.
 * Kept as a standalone export for testability; not part of the composable's public API.
 */
export function extractError(err, fallback) {
    if (err?.response?.data?.errors) {
        return Object.values(err.response.data.errors).flat().join('\n')
    }
    if (err?.response?.data?.error) return err.response.data.error
    if (err?.response?.data?.message) {
        return err.response.data.message.replace(/\s*\(and\s+\d+\s+more\s+errors?\)\s*$/i, '')
    }
    if (err?.message) return err.message
    return fallback || 'Error de conexión'
}

export function useFamilias() {
    const toast = useToast()

    // ==================== LIST SECTION ====================
    const familias = ref({ data: [], meta: {} })
    const loading = ref(false)
    const filters = ref({
        estado: '',
        search: '',
        page: 1,
        per_page: 10,
    })

    async function fetchFamilias() {
        loading.value = true
        const params = {
            filters: { ...filters.value },
        }
        try {
            const res = await store.dispatch(config.getVuexAlias('back.familias.index'), params)
            familias.value = res.data
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') console.error('Error fetching familias:', err)
            familias.value = { data: [], meta: {} }
        } finally {
            loading.value = false
        }
    }

    async function deleteFamilia(familiaId) {
        const params = { familia_id: familiaId, filters: {} }
        try {
            await store.dispatch(config.getVuexAlias('back.familias.destroy'), params)
            toast.success('Familia eliminada')
            await fetchFamilias()
            await fetchPendingCountFamilias()
        } catch (err) {
            toast.error(extractError(err, 'Error al eliminar la familia'))
        }
    }

    async function approveFamily(familiaId) {
        const params = { familia_id: familiaId, filters: {} }
        try {
            await store.dispatch(config.getVuexAlias('back.familias.verificacion.aprobar'), params)
            toast.success('Familia aprobada')
            await fetchFamilias()
            await fetchPendingCountFamilias()
        } catch (err) {
            toast.error(extractError(err, 'Error al aprobar la familia'))
            throw err
        }
    }

    async function rejectFamily(familiaId, motivo) {
        const params = { familia_id: familiaId, filters: {}, motivo }
        try {
            await store.dispatch(config.getVuexAlias('back.familias.verificacion.rechazar'), params)
            toast.success('Familia rechazada')
            await fetchFamilias()
            await fetchPendingCountFamilias()
        } catch (err) {
            toast.error(extractError(err, 'Error al rechazar la familia'))
            throw err
        }
    }

    // ==================== MEMBERS / DETAIL SECTION ====================
    const familia = ref(null)
    const members = ref({ data: [], meta: {} })

    async function fetchFamilia(familiaId) {
        const params = { familia_id: familiaId, filters: {} }
        try {
            const res = await store.dispatch(config.getVuexAlias('back.familias.show'), params)
            familia.value = res.data?.data || res.data
            members.value = { data: familia.value?.miembros || [], meta: {} }
            return familia.value
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') console.error('Error fetching familia:', err)
            familia.value = null
            members.value = { data: [], meta: {} }
            throw err
        }
    }

    async function createFamily(titularAbonadoId, nombre) {
        const params = {
            filters: {},
            titular_abonado_id: titularAbonadoId,
            nombre,
        }
        try {
            const res = await store.dispatch(config.getVuexAlias('back.familias.store'), params)
            toast.success('Familia creada correctamente')
            return res.data
        } catch (err) {
            toast.error(extractError(err, 'Error al crear la familia'))
            throw err
        }
    }

    async function addMember(familiaId, numeroAbonado, dni) {
        const params = { familia_id: familiaId, filters: {}, numero_abonado: numeroAbonado, dni }
        try {
            const res = await store.dispatch(config.getVuexAlias('back.familias.miembros.add'), params)
            toast.success('Miembro añadido')
            familia.value = res.data?.data || res.data
            members.value = { data: familia.value?.miembros || [], meta: {} }
        } catch (err) {
            toast.error(extractError(err, 'Error al añadir miembro'))
            throw err
        }
    }

    async function removeMember(familiaId, miembroId) {
        const params = { familia_id: familiaId, miembro_id: miembroId, filters: {} }
        try {
            const res = await store.dispatch(config.getVuexAlias('back.familias.miembros.remove'), params)
            if (res.data?.deleted) {
                familia.value = null
                members.value = { data: [], meta: {} }
                toast.success(res.data.message || 'Familia eliminada')
            } else {
                familia.value = res.data?.data || res.data
                members.value = { data: familia.value?.miembros || [], meta: {} }
                toast.success('Miembro eliminado')
            }
        } catch (err) {
            toast.error(extractError(err, 'Error al eliminar miembro'))
        }
    }

    async function updateNombre(familiaId, nombre) {
        const params = { familia_id: familiaId, filters: {}, nombre }
        try {
            const res = await store.dispatch(config.getVuexAlias('back.familias.update'), params)
            toast.success('Nombre actualizado correctamente')
            familia.value = res.data?.data || res.data
            members.value = { data: familia.value?.miembros || [], meta: {} }
            await fetchFamilias()
        } catch (err) {
            toast.error(extractError(err, 'Error al actualizar el nombre'))
            throw err
        }
    }

    async function updateNombreByAbonado(abonadoId, nombre) {
        const params = { abonado_id: abonadoId, filters: {}, nombre }
        try {
            await store.dispatch(config.getVuexAlias('back.familias.updateNombreByAbonado'), params)
            toast.success('Nombre actualizado correctamente')
        } catch (err) {
            toast.error(extractError(err, 'Error al actualizar el nombre'))
            throw err
        }
    }

    return {
        // List
        familias,
        loading,
        filters,
        fetchFamilias,
        deleteFamilia,
        approveFamily,
        rejectFamily,

        // Detail / Members
        familia,
        members,
        fetchFamilia,
        createFamily,
        addMember,
        removeMember,

        // Update name
        updateNombre,
        updateNombreByAbonado,

        // Pending count (sidebar badge) — re-exported for callers that already
        // destructure the composable result. `pendingCountFamilias` is also
        // importable as a top-level ref via getPendingCountFamilias().
        pendingCountFamilias,
        fetchPendingCountFamilias,
    }
}
